import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  ClipboardList,
  Plus,
  Trash2,
  Pencil,
  X,
  Check,
  ChevronDown,
  BookmarkPlus,
  BookOpen,
  Loader2,
} from "lucide-react";
import {
  listProjects,
  getOwnerRequirements,
  upsertOwnerRequirement,
  listOwnerReqTemplates,
  getOwnerReqTemplate,
  createOwnerReqTemplate,
  getMeRbac,
  listCompanies,
} from "@/lib/backend";
import type {
  ProjectSummary,
  OwnerRequirementState,
  OwnerRequirementDoc,
  OwnerRequirementTemplate,
} from "@/lib/types";
import { useUser } from "@clerk/clerk-react";
import { toast } from "@/hooks/use-toast";
import {
  buildClientOptions,
  buildLocationOptions,
  filterProjectsForSelection,
  buildProjectOptions,
  normalizeClientKey,
  normalizeLocationKey,
  UNKNOWN_CLIENT_KEY,
} from "@/lib/projectSelection";
import { cloneOwnerRequirementItem } from "@/lib/ownerRequirementItems";
import { useWorkspace } from "@/components/layout/workspace-context";

const DISCIPLINES = [
  "Mechanical",
  "Electrical",
  "Lighting",
  "Plumbing",
  "Fire Protection",
  "Technology",
] as const;

const OWNER_REQUIREMENTS_MISC_KEY = "ALL";
const ALL_DISCIPLINES = [...DISCIPLINES, OWNER_REQUIREMENTS_MISC_KEY] as const;
type DisciplineOption = (typeof ALL_DISCIPLINES)[number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isMiscName(name?: string | null) {
  return (name ?? "").trim().toLowerCase() === "misc";
}

function projectFilter(p: ProjectSummary) {
  return !isMiscName(p.project_name);
}

function projectHasTemplateLocation(project?: ProjectSummary | null): boolean {
  return Boolean(project?.city?.trim() && project?.state?.trim());
}

function normalizeRequirementDiscipline(value?: string | null): string {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return OWNER_REQUIREMENTS_MISC_KEY;
  return trimmed.toLowerCase() === "misc" || trimmed.toLowerCase() === "all"
    ? OWNER_REQUIREMENTS_MISC_KEY
    : trimmed;
}

function getRequirementDisciplineLabel(value?: string | null): string {
  return normalizeRequirementDiscipline(value) === OWNER_REQUIREMENTS_MISC_KEY ? "Misc" : String(value ?? "").trim();
}

function getRequirementDoc(
  docs: Map<string, OwnerRequirementDoc>,
  discipline: string,
): OwnerRequirementDoc | undefined {
  const normalized = normalizeRequirementDiscipline(discipline);
  if (normalized === OWNER_REQUIREMENTS_MISC_KEY) {
    return docs.get(OWNER_REQUIREMENTS_MISC_KEY) ?? docs.get("Misc");
  }
  return docs.get(normalized);
}

function getOwnerRequirementDisplayDate(item: OwnerRequirementState): string {
  const raw = item.updated_at ?? item.created_at ?? null;
  if (!raw) return "Unknown";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString();
}

function getOwnerRequirementSourceLabel(item: OwnerRequirementState): string {
  const source = typeof item.source_name === "string" ? item.source_name.trim() : "";
  return source || "Unknown";
}

const collapsedRequirementTextStyle: React.CSSProperties = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TrackerCheckbox({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (val: boolean) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`
        flex-none mt-0.5 h-4 w-4 rounded border
        ${checked ? "bg-[#7b2ee6] border-[#7b2ee6]" : "bg-transparent border-zinc-600"}
        ${disabled ? "cursor-default" : "cursor-pointer hover:border-[#7b2ee6]"}
        flex items-center justify-center transition-colors
      `}
    >
      {checked && <Check className="h-2.5 w-2.5 text-white" />}
    </button>
  );
}

function ProgressBar({ completedPercent }: { completedPercent: number }) {
  const ticks: Record<number, number> = { 0: 0, 25: 25, 50: 50, 75: 75, 100: 100 };
  const clampedCompleted = Math.max(0, Math.min(completedPercent, 100));
  const tickKeys = Object.keys(ticks).map(Number);

  return (
    <div className="w-full">
      <div className="relative mb-1 h-6 select-none">
        {tickKeys.map((p) => (
          <div
            key={p}
            className="absolute -translate-x-1/2 text-[11px] text-zinc-300"
            style={{ left: `${ticks[p]}%` }}
          >
            {p}%
          </div>
        ))}
      </div>
      <div className="relative h-6 overflow-hidden rounded-xl bg-zinc-800/70 ring-1 ring-[#6c2ad0]/40">
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: "100%",
            backgroundImage:
              "repeating-linear-gradient(135deg, rgba(123,46,230,0.5) 0, rgba(123,46,230,0.5) 8px, rgba(123,46,230,0.2) 8px, rgba(123,46,230,0.2) 16px)",
            backgroundColor: "rgba(73,17,126,0.55)",
            boxShadow:
              "0 0 24px rgba(123,46,230,0.55) inset, 0 0 12px rgba(123,46,230,0.4)",
          }}
        />
        {clampedCompleted > 0 && (
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: `${clampedCompleted}%`,
              backgroundImage:
                "repeating-linear-gradient(135deg, rgba(16,185,129,0.75) 0, rgba(16,185,129,0.75) 8px, rgba(6,95,70,0.8) 8px, rgba(6,95,70,0.8) 16px)",
              backgroundColor: "rgba(6,95,70,0.7)",
              boxShadow:
                "0 0 24px rgba(16,185,129,0.55) inset, 0 0 12px rgba(16,185,129,0.4)",
            }}
          />
        )}
        {tickKeys.map((p) => (
          <div
            key={`tick-${p}`}
            className="absolute bottom-0 top-0 w-px bg-zinc-300/60"
            style={{ left: `${ticks[p]}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function OwnerRequirementsPanel({
  tabId,
  projectId: initialProjectId,
  projectName: initialProjectName,
  clientName: initialClientName,
}: {
  tabId?: string;
  projectId?: string | null;
  projectName?: string | null;
  clientName?: string | null;
}) {
  const { setTabScope } = useWorkspace();
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();

  const userHeaders = useMemo(() => {
    if (!userLoaded || !isSignedIn) return undefined;
    const id = user?.id;
    if (!id) return undefined;
    return { id };
  }, [user?.id, userLoaded, isSignedIn]);

  // ---- Project selection state ----
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [selectedClientKey, setSelectedClientKey] = useState("");
  const [selectedLocationKey, setSelectedLocationKey] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId ?? "");

  // ---- Data state ----
  const [docs, setDocs] = useState<Map<string, OwnerRequirementDoc>>(new Map());
  const [activeDiscipline, setActiveDiscipline] = useState<DisciplineOption>("Mechanical");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---- Edit mode state ----
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<OwnerRequirementState[]>([]);
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());

  // ---- RBAC / company ----
  const [canManageTemplates, setCanManageTemplates] = useState(false);
  const [companyId, setCompanyId] = useState("");

  // ---- Template state ----
  const [templates, setTemplates] = useState<OwnerRequirementTemplate[]>([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateDiscipline, setTemplateDiscipline] = useState<string>(OWNER_REQUIREMENTS_MISC_KEY);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);

  // ---- Dropdown open state ----
  const [clientDropOpen, setClientDropOpen] = useState(false);
  const [locDropOpen, setLocDropOpen] = useState(false);
  const [projDropOpen, setProjDropOpen] = useState(false);
  const clientDropRef = useRef<HTMLDivElement>(null);
  const locDropRef = useRef<HTMLDivElement>(null);
  const projDropRef = useRef<HTMLDivElement>(null);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clientDropRef.current && !clientDropRef.current.contains(e.target as Node)) setClientDropOpen(false);
      if (locDropRef.current && !locDropRef.current.contains(e.target as Node)) setLocDropOpen(false);
      if (projDropRef.current && !projDropRef.current.contains(e.target as Node)) setProjDropOpen(false);
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ---- Derived project selection ----
  const clientOptions = useMemo(
    () => buildClientOptions(projects, { filter: projectFilter }),
    [projects],
  );

  const locationOptions = useMemo(
    () => buildLocationOptions(projects, selectedClientKey, { filter: projectFilter }),
    [projects, selectedClientKey],
  );

  const projectsForLocation = useMemo(
    () =>
      filterProjectsForSelection(
        projects,
        { clientKey: selectedClientKey, locationKey: selectedLocationKey },
        { filter: projectFilter },
      ),
    [projects, selectedClientKey, selectedLocationKey],
  );

  const projectOptions = useMemo(
    () => buildProjectOptions(projectsForLocation),
    [projectsForLocation],
  );

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );
  const selectedProjectHasLocation = useMemo(
    () => projectHasTemplateLocation(selectedProject),
    [selectedProject],
  );

  useEffect(() => {
    if (!tabId) return;
    // Preserve shortcut-provided scope until the dropdown selection initializes.
    if (!selectedProjectId) {
      if (initialProjectId) {
        setTabScope(tabId, {
          projectId: initialProjectId,
          projectName: initialProjectName ?? null,
          clientName: initialClientName ?? null,
          isMiscProject: false,
        });
        return;
      }
      setTabScope(tabId, { projectId: null, projectName: null, clientName: null, isMiscProject: false });
      return;
    }
    setTabScope(tabId, {
      projectId: selectedProjectId,
      projectName: selectedProject?.project_name ?? initialProjectName ?? null,
      clientName: selectedProject?.client_name ?? initialClientName ?? null,
      isMiscProject: false,
    });
  }, [
    tabId,
    selectedProjectId,
    selectedProject,
    setTabScope,
    initialProjectId,
    initialProjectName,
    initialClientName,
  ]);

  useEffect(() => {
    if (!tabId) return;
    // Preserve shortcut-provided scope until the dropdown selection initializes.
    if (!selectedProjectId) {
      if (initialProjectId) {
        setTabScope(tabId, {
          projectId: initialProjectId,
          projectName: initialProjectName ?? null,
          clientName: initialClientName ?? null,
          isMiscProject: false,
        });
        return;
      }
      setTabScope(tabId, { projectId: null, projectName: null, clientName: null, isMiscProject: false });
      return;
    }
    setTabScope(tabId, {
      projectId: selectedProjectId,
      projectName: selectedProject?.project_name ?? initialProjectName ?? null,
      clientName: selectedProject?.client_name ?? initialClientName ?? null,
      isMiscProject: false,
    });
  }, [
    tabId,
    selectedProjectId,
    selectedProject,
    setTabScope,
    initialProjectId,
    initialProjectName,
    initialClientName,
  ]);

  const selectedClientLabel = useMemo(
    () => clientOptions.find((c) => c.key === selectedClientKey)?.label ?? "Select client…",
    [clientOptions, selectedClientKey],
  );

  const selectedLocationLabel = useMemo(
    () => locationOptions.find((l) => l.key === selectedLocationKey)?.label ?? "Select location…",
    [locationOptions, selectedLocationKey],
  );

  const selectedProjectLabel = useMemo(
    () => projectOptions.find((p) => p.value === selectedProjectId)?.label ?? "Select project…",
    [projectOptions, selectedProjectId],
  );

  // ---- Current discipline's items ----
  const currentItems = useMemo(
    () => getRequirementDoc(docs, activeDiscipline)?.items ?? [],
    [docs, activeDiscipline],
  );

  const completedCount = useMemo(
    () => currentItems.filter((item) => item.done).length,
    [currentItems],
  );

  const progressPercent = useMemo(() => {
    if (currentItems.length === 0) return 0;
    return Math.round((completedCount / currentItems.length) * 100);
  }, [completedCount, currentItems.length]);

  const requirementColumnHeader = useMemo(
    () => `${getRequirementDisciplineLabel(activeDiscipline).toUpperCase()} REQUIREMENTS`,
    [activeDiscipline],
  );

  // ---- Load projects + RBAC ----
  useEffect(() => {
    if (!userHeaders) {
      setProjects([]);
      setCanManageTemplates(false);
      setCompanyId("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [projectData, rbacResp, companiesData] = await Promise.all([
          listProjects({}, userHeaders),
          getMeRbac(userHeaders),
          listCompanies(userHeaders),
        ]);
        if (cancelled) return;
        const list = Array.isArray(projectData) ? projectData.filter(projectFilter) : [];
        setProjects(list);
        const perms = Array.isArray((rbacResp as any)?.rbac?.permissions)
          ? ((rbacResp as any).rbac.permissions as string[])
          : [];
        setCanManageTemplates(perms.includes("company_tracker_templates:manage"));
        const cId = Array.isArray(companiesData) && companiesData.length > 0 ? companiesData[0].id : "";
        setCompanyId(cId);
      } catch {
        if (!cancelled) {
          setProjects([]);
          setCanManageTemplates(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userHeaders]);

  // ---- Auto-select from initialProjectId ----
  useEffect(() => {
    if (!initialProjectId || !projects.length) return;
    const project = projects.find((p) => p.id === initialProjectId);
    if (!project) return;
    setSelectedClientKey(normalizeClientKey(project));
    setSelectedLocationKey(normalizeLocationKey(project));
    setSelectedProjectId(project.id);
  }, [initialProjectId, projects]);

  // ---- Auto-select first project when client/location change ----
  useEffect(() => {
    if (!selectedClientKey || !selectedLocationKey) return;
    if (projectOptions.length > 0 && !projectOptions.find((p) => p.value === selectedProjectId)) {
      setSelectedProjectId(projectOptions[0].value);
    }
  }, [selectedClientKey, selectedLocationKey, projectOptions]);

  // ---- Load owner requirements when project changes ----
  useEffect(() => {
    if (!selectedProjectId || !userHeaders?.id) {
      setDocs(new Map());
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await getOwnerRequirements(selectedProjectId, userHeaders);
        if (cancelled) return;
        const map = new Map<string, OwnerRequirementDoc>();
        for (const doc of data) map.set(doc.discipline, doc);
        setDocs(map);
      } catch (e) {
        if (!cancelled) console.error("[OwnerReqs] failed to load", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProjectId, userHeaders]);

  // ---- Load templates ----
  const loadTemplates = useCallback(async () => {
    if (!companyId || !userHeaders?.id || !selectedProjectHasLocation || !selectedProject) {
      setTemplates([]);
      return;
    }
    try {
      const data = await listOwnerReqTemplates(
        companyId,
        {
          city: selectedProject.city ?? undefined,
          state: selectedProject.state ?? undefined,
        },
        userHeaders,
      );
      setTemplates(data);
    } catch {
      setTemplates([]);
    }
  }, [companyId, userHeaders, selectedProject, selectedProjectHasLocation]);

  useEffect(() => {
    if (companyId && userHeaders?.id) loadTemplates();
    else setTemplates([]);
  }, [companyId, userHeaders, loadTemplates]);

  // Exit edit mode when project changes
  useEffect(() => {
    setEditMode(false);
    setDraft([]);
  }, [selectedProjectId]);

  useEffect(() => {
    setExpandedItemIds(new Set());
  }, [selectedProjectId, activeDiscipline, editMode]);

  useEffect(() => {
    setExpandedItemIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (currentItems.some((item) => item.id === id)) next.add(id);
      }
      return next;
    });
  }, [currentItems]);

  // ---- Derived capability gates (per-project, same pattern as ProjectTrackerPanel) ----

  // Structural edits: add, remove, rename requirements — requires manage permission.
  const canEdit = Boolean(selectedProject?.capabilities?.project_trackers_manage);

  // Checkbox toggles: mark a requirement done/undone — requires update or manage.
  const canToggle = Boolean(
    selectedProject?.capabilities?.project_trackers_update ||
    selectedProject?.capabilities?.project_trackers_manage,
  );

  // ---- Handlers ----

  const enterEditMode = useCallback(() => {
    setDraft(currentItems.map((i) => ({ ...i })));
    setEditMode(true);
  }, [currentItems]);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
    setDraft([]);
  }, []);

  const toggleItemExpanded = useCallback((itemId: string) => {
    setExpandedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedProjectId || !userHeaders?.id) return;
    setSaving(true);
    try {
      const saved = await upsertOwnerRequirement(selectedProjectId, activeDiscipline, draft, userHeaders);
      setDocs((prev) => {
        const next = new Map(prev);
        next.set(activeDiscipline, saved);
        return next;
      });
      setEditMode(false);
      toast({ title: "Owner requirements saved" });
    } catch {
      toast({ title: "Failed to save owner requirements", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }, [selectedProjectId, activeDiscipline, draft, userHeaders]);

  const handleToggle = useCallback(async (itemId: string) => {
    if (!selectedProjectId || !userHeaders?.id || !canToggle) return;
    const updated = currentItems.map((i) => i.id === itemId ? { ...i, done: !i.done } : i);
    // optimistic update
    setDocs((prev) => {
      const next = new Map(prev);
      const existing = getRequirementDoc(prev, activeDiscipline);
      if (existing) next.set(normalizeRequirementDiscipline(activeDiscipline), { ...existing, items: updated });
      return next;
    });
    try {
      const disciplineKey = normalizeRequirementDiscipline(activeDiscipline);
      const saved = await upsertOwnerRequirement(selectedProjectId, disciplineKey, updated, userHeaders);
      setDocs((prev) => {
        const next = new Map(prev);
        next.set(disciplineKey, saved);
        return next;
      });
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    }
  }, [selectedProjectId, activeDiscipline, currentItems, userHeaders, canToggle]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (!companyId || !userHeaders?.id) return;
    if (!selectedProject || !selectedProjectHasLocation) return;
    setSavingTemplate(true);
    try {
      await createOwnerReqTemplate(
        companyId,
        {
          city: selectedProject.city ?? "",
          state: selectedProject.state ?? "",
          discipline: normalizeRequirementDiscipline(templateDiscipline),
          items: currentItems.map(cloneOwnerRequirementItem),
        },
        userHeaders,
      );
      await loadTemplates();
      setShowSaveTemplateModal(false);
      toast({ title: "Template saved" });
    } catch (e: any) {
      const msg = e?.message?.includes("409") || String(e).includes("409")
        ? "A template with these settings already exists. Delete the existing one first."
        : "Failed to save template";
      toast({ title: msg, variant: "destructive" });
    } finally {
      setSavingTemplate(false);
    }
  }, [companyId, userHeaders, selectedProject, selectedProjectHasLocation, templateDiscipline, currentItems, loadTemplates]);

  const handleLoadTemplate = useCallback(async (template: OwnerRequirementTemplate) => {
    if (!companyId || !selectedProjectId || !userHeaders?.id || !canEdit) return;
    setSaving(true);
    try {
      const fullTemplate = await getOwnerReqTemplate(companyId, template.id, userHeaders);
      if (fullTemplate.items.length === 0) {
        toast({ title: "Template has no items to apply", variant: "destructive" });
        return;
      }
      const normalizedDiscipline = normalizeRequirementDiscipline(fullTemplate.discipline);
      const saved = await upsertOwnerRequirement(
        selectedProjectId,
        normalizedDiscipline,
        fullTemplate.items.map(cloneOwnerRequirementItem),
        userHeaders,
      );
      setDocs((prev) => {
        const next = new Map(prev);
        next.set(normalizedDiscipline, saved);
        return next;
      });
      setActiveDiscipline(normalizedDiscipline as DisciplineOption);
      setTemplateDropdownOpen(false);
      toast({ title: `Loaded template for ${getRequirementDisciplineLabel(fullTemplate.discipline)}` });
    } catch {
      toast({ title: "Failed to load template", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }, [companyId, selectedProjectId, userHeaders, canEdit]);

  // ---- Render ----

  const isLoaded = userLoaded;

  return (
    <div className="flex h-full flex-col bg-[#0d1117] text-zinc-100 select-none overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
        <ClipboardList className="h-4 w-4 text-purple-400" />
        <span className="text-sm font-semibold tracking-wide text-zinc-100">Owner Requirements</span>
      </div>

      {/* Project selector */}
      <div className="border-b border-zinc-800 px-4 py-3 space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-400">Project</p>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* Client */}
            <div className="relative" ref={clientDropRef}>
              <button
                type="button"
                onClick={() => setClientDropOpen((v) => !v)}
                className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800/60 px-3 py-1.5 text-xs text-zinc-200 hover:border-zinc-500 transition-colors"
              >
                <span className="max-w-[140px] truncate">{selectedClientLabel}</span>
                <ChevronDown className="h-3 w-3 text-zinc-400" />
              </button>
              {clientDropOpen && (
                <div className="absolute top-full left-0 z-30 mt-1 max-h-48 min-w-[160px] overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 shadow-xl">
                  {clientOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        setSelectedClientKey(opt.key);
                        setSelectedLocationKey("");
                        setSelectedProjectId("");
                        setClientDropOpen(false);
                      }}
                      className={`block w-full px-3 py-2 text-left text-xs hover:bg-zinc-800 transition-colors ${opt.key === selectedClientKey ? "text-purple-400" : "text-zinc-200"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  {clientOptions.length === 0 && (
                    <p className="px-3 py-2 text-xs text-zinc-500">No clients</p>
                  )}
                </div>
              )}
            </div>

            {/* Location */}
            {selectedClientKey && selectedClientKey !== UNKNOWN_CLIENT_KEY && (
              <div className="relative" ref={locDropRef}>
                <button
                  type="button"
                  onClick={() => setLocDropOpen((v) => !v)}
                  className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800/60 px-3 py-1.5 text-xs text-zinc-200 hover:border-zinc-500 transition-colors"
                >
                  <span className="max-w-[140px] truncate">{selectedLocationLabel}</span>
                  <ChevronDown className="h-3 w-3 text-zinc-400" />
                </button>
                {locDropOpen && (
                  <div className="absolute top-full left-0 z-30 mt-1 max-h-48 min-w-[160px] overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 shadow-xl">
                    {locationOptions.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          setSelectedLocationKey(opt.key);
                          setSelectedProjectId("");
                          setLocDropOpen(false);
                        }}
                        className={`block w-full px-3 py-2 text-left text-xs hover:bg-zinc-800 transition-colors ${opt.key === selectedLocationKey ? "text-purple-400" : "text-zinc-200"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Project */}
            {selectedClientKey && selectedLocationKey && (
              <div className="relative" ref={projDropRef}>
                <button
                  type="button"
                  onClick={() => setProjDropOpen((v) => !v)}
                  className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800/60 px-3 py-1.5 text-xs text-zinc-200 hover:border-zinc-500 transition-colors"
                >
                  <span className="max-w-[180px] truncate">{selectedProjectLabel}</span>
                  <ChevronDown className="h-3 w-3 text-zinc-400" />
                </button>
                {projDropOpen && (
                  <div className="absolute top-full left-0 z-30 mt-1 max-h-48 min-w-[200px] overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 shadow-xl">
                    {projectOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setSelectedProjectId(opt.value);
                          setProjDropOpen(false);
                        }}
                        className={`block w-full px-3 py-2 text-left text-xs hover:bg-zinc-800 transition-colors ${opt.value === selectedProjectId ? "text-purple-400" : "text-zinc-200"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                    {projectOptions.length === 0 && (
                      <p className="px-3 py-2 text-xs text-zinc-500">No projects</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedProjectId && (
            <div className="ml-auto flex items-center gap-2">
              {canEdit && !editMode && (
                <>
                  {canManageTemplates && currentItems.length > 0 && selectedProjectHasLocation && (
                    <button
                      type="button"
                      onClick={() => {
                        setTemplateDiscipline(
                          activeDiscipline === OWNER_REQUIREMENTS_MISC_KEY ? OWNER_REQUIREMENTS_MISC_KEY : activeDiscipline,
                        );
                        setShowSaveTemplateModal(true);
                      }}
                      title="Save as template"
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-purple-400 transition-colors"
                    >
                      <BookmarkPlus className="h-3.5 w-3.5" />
                      Save as template
                    </button>
                  )}
                  {templates.length > 0 && (
                    <div className="relative" ref={templateDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setTemplateDropdownOpen((open) => !open)}
                        title="Load from template"
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-purple-400 transition-colors"
                      >
                        <BookOpen className="h-3.5 w-3.5" />
                        Load template
                        <ChevronDown className={`h-3 w-3 transition-transform ${templateDropdownOpen ? "rotate-180" : ""}`} />
                      </button>
                      {templateDropdownOpen && (
                        <div className="absolute right-0 top-full z-30 mt-1 max-h-80 min-w-[260px] overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-900 py-1 shadow-xl">
                          {templates.length === 0 ? (
                            <p className="px-3 py-2 text-xs italic text-zinc-500">
                              No templates found for this location.
                            </p>
                          ) : (
                            templates.map((t) => (
                              <div
                                key={t.id}
                                className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-zinc-800"
                              >
                                <button
                                  type="button"
                                  onClick={() => handleLoadTemplate(t)}
                                  disabled={saving}
                                  className="min-w-0 flex-1 text-left disabled:opacity-50"
                                >
                                  <p className="truncate text-xs font-medium text-zinc-100">
                                    {getRequirementDisciplineLabel(t.discipline)}
                                  </p>
                                  <p className="text-[11px] text-zinc-500">
                                    {t.items.length} item{t.items.length !== 1 ? "s" : ""}
                                    {t.city && t.state ? ` · ${t.city}, ${t.state}` : ""}
                                  </p>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleLoadTemplate(t)}
                                  disabled={saving}
                                  className="rounded-md bg-purple-600/80 px-2 py-1 text-[11px] font-medium text-white transition-colors hover:bg-purple-500 disabled:opacity-50"
                                >
                                  Apply
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={enterEditMode}
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-purple-400 transition-colors"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Discipline tabs */}
      {selectedProjectId && (
        <div className="flex items-center gap-0.5 overflow-x-auto border-b border-zinc-800 px-3 pt-2 pb-0 no-scrollbar">
          {ALL_DISCIPLINES.map((d) => {
            const hasItems = (getRequirementDoc(docs, d)?.items?.length ?? 0) > 0;
            return (
              <button
                key={d}
                type="button"
                onClick={() => {
                  if (editMode) cancelEdit();
                  setActiveDiscipline(d);
                }}
                className={`
                  flex-none px-3 py-1.5 text-[11px] font-medium rounded-t-md border-b-2 transition-colors whitespace-nowrap
                  ${activeDiscipline === d
                    ? "border-purple-500 text-purple-300 bg-zinc-800/50"
                    : "border-transparent text-zinc-500 hover:text-zinc-300 hover:border-zinc-600"
                  }
                `}
              >
                {getRequirementDisciplineLabel(d)}
                {hasItems && (
                  <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-purple-400" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 overflow-auto px-4 py-4">
        {!selectedProjectId ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-zinc-500">Select a project to view owner requirements.</p>
          </div>
        ) : loading ? (
          <div className="flex h-full items-center justify-center gap-2 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-zinc-800 bg-black/30 px-4 py-4 shadow-[0_0_24px_rgba(123,46,230,0.08)]">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-zinc-100">
                    {getRequirementDisciplineLabel(activeDiscipline)} Progress
                  </p>
                  <p className="text-xs text-zinc-400">
                    {completedCount}/{currentItems.length} requirement{currentItems.length === 1 ? "" : "s"} complete
                  </p>
                </div>
                <span className="text-sm font-semibold text-emerald-300">{progressPercent}%</span>
              </div>
              <ProgressBar completedPercent={progressPercent} />
            </div>

            {/* View mode */}
            {!editMode && (
              <div>
                {currentItems.length === 0 ? (
                  <p className="text-sm text-zinc-500 italic">
                    No requirements for {getRequirementDisciplineLabel(activeDiscipline)}.
                    {canEdit && " Click Edit to add some."}
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-zinc-800 bg-black/20">
                    <div className="grid grid-cols-[minmax(0,1fr)_11rem_7rem] border-b border-zinc-800 bg-zinc-900/60 py-2 text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
                      <span className="block min-w-0 truncate px-3" title={requirementColumnHeader}>
                        {requirementColumnHeader}
                      </span>
                      <span className="px-3">Source</span>
                      <span className="px-3">Date</span>
                    </div>
                    {currentItems.map((item) => (
                      <div
                        key={item.id}
                        className="grid grid-cols-[minmax(0,1fr)_11rem_7rem] border-b border-zinc-800/70 last:border-b-0"
                      >
                        <div className="flex min-w-0 items-start gap-3 px-3 py-3">
                          <div
                            className="pt-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TrackerCheckbox checked={item.done} disabled={!canToggle} onChange={() => handleToggle(item.id)} />
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleItemExpanded(item.id)}
                            className="min-w-0 flex-1 text-left"
                            title={expandedItemIds.has(item.id) ? "Collapse requirement" : "Expand requirement"}
                          >
                            <span
                              className={`block text-sm leading-5 ${item.done ? "line-through text-zinc-500" : "text-zinc-200"} ${!expandedItemIds.has(item.id) ? "cursor-pointer" : ""}`}
                              style={expandedItemIds.has(item.id) ? undefined : collapsedRequirementTextStyle}
                            >
                              {item.text}
                            </span>
                          </button>
                        </div>
                        <div className={`px-3 py-3 text-xs ${item.done ? "text-zinc-600" : "text-zinc-400"}`}>
                          {getOwnerRequirementSourceLabel(item)}
                        </div>
                        <div className={`px-3 py-3 text-xs ${item.done ? "text-zinc-600" : "text-zinc-400"}`}>
                          {getOwnerRequirementDisplayDate(item)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Edit mode */}
            {editMode && (
              <div className="space-y-3">
                <ul className="space-y-2">
                  {draft.map((item, idx) => (
                    <li key={item.id} className="flex items-start gap-2">
                      <TrackerCheckbox checked={item.done} disabled />
                      <input
                        type="text"
                        value={item.text}
                        autoFocus={idx === draft.length - 1 && item.text === ""}
                        onChange={(e) =>
                          setDraft((prev) =>
                            prev.map((r) => r.id === item.id ? { ...r, text: e.target.value } : r),
                          )
                        }
                        className="flex-1 rounded border border-zinc-700 bg-zinc-800/60 px-2 py-1 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder="Requirement text…"
                      />
                      <button
                        type="button"
                        onClick={() => setDraft((prev) => prev.filter((r) => r.id !== item.id))}
                        className="flex-none p-1 rounded hover:bg-red-500/20 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-zinc-500 hover:text-red-400" />
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((prev) => [
                          ...prev,
                          { id: `req-${Date.now()}-${Math.random().toString(36).slice(2)}`, text: "", done: false },
                        ])
                      }
                      className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add requirement
                    </button>
                  </li>
                </ul>

                {/* Edit actions */}
                <div className="flex items-center gap-2 justify-end pt-2 border-t border-zinc-800">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="rounded-lg bg-zinc-800 px-4 py-1.5 text-sm font-medium text-zinc-300 hover:bg-zinc-700 transition-colors ring-1 ring-zinc-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 rounded-lg bg-[#7b2ee6] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#6c2ad0] disabled:opacity-50 transition-colors"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save as Template Modal */}
      {showSaveTemplateModal && selectedProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
          onClick={() => setShowSaveTemplateModal(false)}
        >
          <div
            className="relative w-full max-w-md rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-zinc-800">
              <h3 className="text-sm font-semibold text-zinc-100">Save as Template</h3>
              <button type="button" onClick={() => setShowSaveTemplateModal(false)} className="p-1 rounded hover:bg-zinc-800">
                <X className="h-4 w-4 text-zinc-400" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-xs text-zinc-400">
                This template will be available to any permitted user working on a project in{" "}
                <strong className="text-zinc-200">
                  {selectedProject.city ?? "?"}, {selectedProject.state ?? "?"}
                </strong>
                .
              </p>
              <div>
                <p className="mb-1 text-xs text-zinc-400">Discipline</p>
                <select
                  value={templateDiscipline}
                  onChange={(e) => setTemplateDiscipline(e.target.value)}
                  className="w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-purple-500"
                >
                  {ALL_DISCIPLINES.map((d) => (
                    <option key={d} value={d}>{getRequirementDisciplineLabel(d)}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-zinc-500">
                  Use "Misc" for requirements that apply regardless of discipline.
                </p>
              </div>
              <p className="text-xs text-zinc-400">
                Items from the current <strong className="text-zinc-200">{getRequirementDisciplineLabel(activeDiscipline)}</strong> view ({currentItems.length} item{currentItems.length !== 1 ? "s" : ""}) will be saved.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => setShowSaveTemplateModal(false)}
                className="rounded-lg bg-zinc-800 px-4 py-1.5 text-sm font-medium text-zinc-300 hover:bg-zinc-700 transition-colors ring-1 ring-zinc-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate}
                className="flex items-center gap-1.5 rounded-lg bg-[#7b2ee6] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#6c2ad0] disabled:opacity-50 transition-colors"
              >
                {savingTemplate ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {savingTemplate ? "Saving…" : "Save Template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
